import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  PhoneIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { exemptPhoneNumbersAPI } from '../api/exemptPhoneNumbersAPI';

const EMPTY_FORM = { phone_number: '', description: '' };

const inputCls =
  'w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

/** Любой ввод (8 777…, 7777…, +7 (777) …) → +77771234567, как хранится у пользователей. */
const normalizePhone = (raw) => {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  if (digits.length === 10) digits = `7${digits}`;
  return digits ? `+${digits}` : '';
};

// Халықаралық формат: App Store тексерушілеріне шетелдік тест нөмір берілуі мүмкін
const isValidPhone = (normalized) => /^\+\d{10,15}$/.test(normalized);

/** +77771234567 → +7 777 123 45 67 */
const formatPhone = (phone) => {
  const m = /^\+7(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(phone || '');
  return m ? `+7 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : phone;
};

const humanizeError = (err, fallback) => {
  if (err?.status === 400 && /already exists/i.test(err.message)) return 'Этот номер уже есть в списке.';
  if (err?.status === 403) return 'Недостаточно прав.';
  return err?.message && !/^HTTP error/.test(err.message) ? err.message : fallback;
};

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Скопировать"
      aria-label="Скопировать номер"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard недоступен */
        }
      }}
      className="rounded p-1 text-text-muted opacity-0 transition-opacity hover:bg-bg-tertiary hover:text-text-primary focus:opacity-100 group-hover:opacity-100"
    >
      {copied ? <CheckIcon className="h-4 w-4 text-emerald-500" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
    </button>
  );
};

const ExemptPhoneNumbers = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [toast, setToast] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');
  const addInputRef = useRef(null);

  const notify = useCallback((type, text) => setToast({ type, text }), []);

  useEffect(() => {
    if (!toast || toast.type === 'error') return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchPhoneNumbers = useCallback(async () => {
    setState((s) => (s === 'ready' ? 'ready' : 'loading'));
    try {
      const data = await exemptPhoneNumbersAPI.getAll();
      const list = (data.exempt_phone_numbers || []).sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setPhoneNumbers(list);
      setState('ready');
    } catch (err) {
      console.error('Error fetching phone numbers:', err);
      setState('error');
    }
  }, []);

  useEffect(() => {
    fetchPhoneNumbers();
  }, [fetchPhoneNumbers]);

  const openAddForm = () => {
    setShowAddForm(true);
    setAddError(null);
    setTimeout(() => addInputRef.current?.focus(), 0);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setAddForm(EMPTY_FORM);
    setAddError(null);
  };

  const addNormalized = normalizePhone(addForm.phone_number);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!isValidPhone(addNormalized)) {
      setAddError('Введите номер в международном формате, например +77771234567');
      return;
    }
    if (phoneNumbers.some((p) => p.phone_number === addNormalized)) {
      setAddError('Этот номер уже есть в списке.');
      return;
    }
    setAdding(true);
    try {
      await exemptPhoneNumbersAPI.add({ phone_number: addNormalized, description: addForm.description.trim() });
      notify('success', `Номер ${formatPhone(addNormalized)} добавлен`);
      closeAddForm();
      fetchPhoneNumbers();
    } catch (err) {
      console.error('Error adding phone number:', err);
      setAddError(humanizeError(err, 'Ошибка добавления номера'));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setConfirmDeleteId(null);
    setEditingId(item.id);
    setEditForm({ phone_number: item.phone_number, description: item.description || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleUpdate = async (id) => {
    const normalized = normalizePhone(editForm.phone_number);
    if (!isValidPhone(normalized)) {
      notify('error', 'Введите номер в международном формате, например +77771234567');
      return;
    }
    setBusyId(id);
    try {
      await exemptPhoneNumbersAPI.update(id, { phone_number: normalized, description: editForm.description.trim() });
      notify('success', 'Изменения сохранены');
      cancelEdit();
      fetchPhoneNumbers();
    } catch (err) {
      console.error('Error updating phone number:', err);
      notify('error', humanizeError(err, 'Ошибка обновления номера'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    setBusyId(item.id);
    try {
      await exemptPhoneNumbersAPI.delete(item.id);
      setPhoneNumbers((list) => list.filter((p) => p.id !== item.id));
      notify('success', `Номер ${formatPhone(item.phone_number)} удалён — вход снова по SMS`);
    } catch (err) {
      console.error('Error deleting phone number:', err);
      notify('error', humanizeError(err, 'Ошибка удаления номера'));
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return phoneNumbers;
    const qDigits = q.replace(/\D/g, '');
    return phoneNumbers.filter(
      (p) =>
        (qDigits && p.phone_number.replace(/\D/g, '').includes(qDigits)) ||
        (p.description || '').toLowerCase().includes(q)
    );
  }, [phoneNumbers, query]);

  return (
    <div className="space-y-4 text-left">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Номера без SMS</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Тестовые номера, которые входят в приложение без SMS-кода</p>
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={openAddForm}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <PlusIcon className="h-4 w-4" />
            Добавить номер
          </button>
        )}
      </div>

      {/* Security warning */}
      <div className="flex gap-3 rounded-block border border-amber-500/30 bg-amber-500/10 px-4 py-3">
        <ShieldExclamationIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div className="text-sm">
          <p className="font-semibold text-amber-500">Вход без проверки</p>
          <p className="mt-0.5 text-text-secondary">
            На эти номера SMS не отправляется, и вход проходит с <b className="text-text-primary">любым кодом</b>.
            Добавляйте только тестовые номера (например, для проверки App Store / Google Play) и удаляйте их, когда они больше не нужны.
          </p>
        </div>
      </div>

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === 'error'
              ? 'border-rose-500/30 bg-rose-500/10 text-rose-500'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <form
          onSubmit={handleAdd}
          onKeyDown={(e) => e.key === 'Escape' && closeAddForm()}
          noValidate
          className="rounded-block border border-[#4F45E5]/40 bg-bg-card p-5 shadow-sm"
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-text-primary">Новый номер</h3>
            <button
              type="button"
              onClick={closeAddForm}
              aria-label="Закрыть"
              className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[16rem_minmax(0,1fr)_auto] md:items-start">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-text-secondary">Номер телефона</span>
              <div className="relative">
                <PhoneIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                <input
                  ref={addInputRef}
                  type="tel"
                  inputMode="tel"
                  placeholder="+7 777 123 45 67"
                  value={addForm.phone_number}
                  onChange={(e) => {
                    setAddForm((f) => ({ ...f, phone_number: e.target.value }));
                    setAddError(null);
                  }}
                  aria-invalid={Boolean(addError)}
                  className={`${inputCls} pl-9 font-mono ${addError ? 'border-rose-500/60' : ''}`}
                />
              </div>
              {addError ? (
                <span className="mt-1 block text-xs text-rose-500">{addError}</span>
              ) : (
                addForm.phone_number &&
                addNormalized !== addForm.phone_number && (
                  <span className="mt-1 block text-xs text-text-muted">
                    Будет сохранён как <span className="font-mono text-text-secondary">{addNormalized}</span>
                  </span>
                )
              )}
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-text-secondary">Описание</span>
              <input
                type="text"
                placeholder="Например: App Store review"
                value={addForm.description}
                onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                className={inputCls}
              />
            </label>
            <div className="flex gap-2 md:pt-[1.375rem]">
              <button
                type="submit"
                disabled={adding}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 md:flex-none"
              >
                {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                Сохранить
              </button>
              <button
                type="button"
                onClick={closeAddForm}
                className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
              >
                Отмена
              </button>
            </div>
          </div>
        </form>
      )}

      {/* List */}
      <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-primary px-5 py-3">
          <h3 className="text-base font-semibold text-text-primary">
            Номера
            {state === 'ready' && <span className="ml-2 text-sm font-normal text-text-muted">{phoneNumbers.length}</span>}
          </h3>
          <div className="flex items-center gap-2">
            {phoneNumbers.length > 5 && (
              <div className="relative w-56">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Номер или описание"
                  aria-label="Поиск"
                  className={`${inputCls} py-1.5 pl-8`}
                />
              </div>
            )}
            <button
              type="button"
              onClick={fetchPhoneNumbers}
              title="Обновить"
              aria-label="Обновить"
              className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary"
            >
              <ArrowPathIcon className={`h-4 w-4 ${state === 'loading' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {state === 'loading' && (
          <ul className="divide-y divide-border-primary">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="flex animate-pulse items-center gap-4 px-5 py-4">
                <div className="h-4 w-36 rounded bg-bg-tertiary" />
                <div className="h-3 flex-1 rounded bg-bg-tertiary" />
                <div className="h-3 w-20 rounded bg-bg-tertiary" />
              </li>
            ))}
          </ul>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
            <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
            <p className="text-sm text-text-secondary">Не удалось загрузить номера</p>
            <button
              type="button"
              onClick={fetchPhoneNumbers}
              className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Повторить
            </button>
          </div>
        )}

        {state === 'ready' && phoneNumbers.length === 0 && (
          <div className="px-6 py-14 text-center">
            <PhoneIcon className="mx-auto mb-3 h-10 w-10 text-text-muted" />
            <p className="text-sm font-medium text-text-primary">Список пуст</p>
            <p className="mt-1 text-sm text-text-secondary">Все пользователи входят по SMS-коду.</p>
          </div>
        )}

        {state === 'ready' && phoneNumbers.length > 0 && filtered.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-text-muted">Ничего не найдено</div>
        )}

        {state === 'ready' && filtered.length > 0 && (
          <ul className="divide-y divide-border-primary">
            {filtered.map((item) => {
              const editing = editingId === item.id;
              const confirming = confirmDeleteId === item.id;
              const busy = busyId === item.id;
              return (
                <li
                  key={item.id}
                  className={`group px-5 py-3 transition-colors ${
                    editing ? 'bg-[#4F45E5]/[0.06]' : confirming ? 'bg-rose-500/[0.06]' : 'hover:bg-bg-tertiary/50'
                  } ${busy ? 'pointer-events-none opacity-60' : ''}`}
                >
                  {editing ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleUpdate(item.id);
                      }}
                      onKeyDown={(e) => e.key === 'Escape' && cancelEdit()}
                      className="flex flex-col gap-2 md:flex-row md:items-center"
                    >
                      <input
                        type="tel"
                        autoFocus
                        value={editForm.phone_number}
                        onChange={(e) => setEditForm((f) => ({ ...f, phone_number: e.target.value }))}
                        aria-label="Номер телефона"
                        className={`${inputCls} font-mono md:w-56`}
                      />
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                        placeholder="Описание"
                        aria-label="Описание"
                        className={`${inputCls} md:flex-1`}
                      />
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-accent-grad px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                        >
                          <CheckIcon className="h-4 w-4" />
                          Сохранить
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="rounded-lg border border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
                        >
                          Отмена
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <div className="flex items-center gap-1 md:w-56">
                        <span className="font-mono text-sm font-semibold text-text-primary">{formatPhone(item.phone_number)}</span>
                        <CopyButton value={item.phone_number} />
                      </div>
                      <div className="order-last min-w-0 basis-full text-sm text-text-secondary md:order-none md:basis-auto md:flex-1">
                        {item.description || <span className="italic text-text-muted">Без описания</span>}
                      </div>
                      <span className="ml-auto text-xs text-text-muted md:ml-0 md:w-24 md:text-right">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('ru-RU') : ''}
                      </span>
                      {confirming ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-rose-500">Удалить?</span>
                          <button
                            type="button"
                            autoFocus
                            onClick={() => handleDelete(item)}
                            className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                          >
                            Да, удалить
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="rounded-lg border border-border-primary px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-tertiary"
                          >
                            Нет
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            title="Редактировать"
                            aria-label="Редактировать"
                            className="rounded-lg p-2 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              cancelEdit();
                              setConfirmDeleteId(item.id);
                            }}
                            title="Удалить"
                            aria-label="Удалить"
                            className="rounded-lg p-2 text-text-muted hover:bg-rose-500/10 hover:text-rose-500"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ExemptPhoneNumbers;
